<template>
    <div>
      <div class="p-2 row g-4">
        <div class="col-sm-6 col-xl-3">
          <SummaryBox type="currency" iconType="fa-solid fa-sack-dollar" title="Total Interest" :amount="interestSummary.total" />
        </div>
        <div class="col-sm-6 col-xl-3">
          <SummaryBox type="percent" iconType="fa-solid fa-percent" title="AVG Amount" :amount="interestSummary.avgAmount" />
        </div>
      </div>
  
      <div class="p-2">
        <v-card flat rounded="lg" class="bond-history-card">
          <v-card-title class="d-flex align-center justify-space-between px-4 py-3">
            <span class="text-h6 font-weight-bold">Bond Interest History</span>

            <v-btn color="primary" variant="tonal" rounded="lg" @click="openAddDialog">
              <font-awesome-icon icon="plus" class="mr-2" />Add Record
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

          <v-data-table
            v-else
            :headers="headers"
            :items="interestHistory"
            item-value="entity_id"
            class="custom-data-table"
            :loading="loading"
            no-data-text="No interest history available."
          >
            <template v-slot:[`item.actions`]="{ item }">
                <font-awesome-icon icon="edit" class="mr-2 cursor-pointer" @click="editItem(item)" />
                <font-awesome-icon icon="trash" class="cursor-pointer" @click="deleteItem(item)" />
            </template>
          </v-data-table>
  
          <v-dialog v-model="dialog" max-width="600px">
            <v-card>
              <v-card-title>
                <span class="headline">{{ editedIndex.value === -1 ? 'Add' : 'Edit' }} Record</span>
              </v-card-title>
  
              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col cols="12" sm="6" v-for="field in fields" :key="field.model">
                      <v-text-field
                        v-if="!field.select"
                        v-model="editedItem[field.model]"
                        :label="field.label"
                        :type="field.type || 'text'"
                        variant="outlined"
                        :disabled="field.readonly || false"
                      />
  
                      <v-select
                        v-else-if="field.select"
                        v-model="editedItem[field.model]"
                        :items="field.items"
                        :label="field.label"
                        variant="outlined"
                      />
  
                      <v-textarea
                        v-else-if="field.textarea"
                        v-model="editedItem[field.model]"
                        :label="field.label"
                        variant="outlined"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
  
              <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
                <v-btn color="primary" variant="tonal" @click="save">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </div>
    </div>
  </template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_BOND_INTEREST_HISTORY, ADD_BOND_INTEREST_HISTORY, UPDATE_BOND_INTEREST_HISTORY, DELETE_BOND_INTEREST_HISTORY } from '@/graphql/bonds'
import SummaryBox from '@/components/Summary/Box.ce.vue'

const interestHistory = ref([])
const dialog = ref(false)
const editedIndex = ref(-1)
const editedItem = ref({})
const loading = ref(false)

// Query bond interest history
const { refetch } = useQuery(GET_BOND_INTEREST_HISTORY)
// Mutations
const { mutate: addInterest } = useMutation(ADD_BOND_INTEREST_HISTORY)
const { mutate: updateInterest } = useMutation(UPDATE_BOND_INTEREST_HISTORY)
const { mutate: deleteInterest } = useMutation(DELETE_BOND_INTEREST_HISTORY)

onMounted(() => {
  fetchBonds()
})

// Headers for table
const headers = [
  { title: 'Bond ID', key: 'bond_id' },
  { title: 'Payment Date', key: 'date' },
  { title: 'Amount', key: 'amount' },
  { title: 'Type', key: 'type' },
  { title: 'Note', key: 'note' },
  { title: 'Actions', key: 'actions', sortable: false },
]

// Fields for dialog
const fields = [
  { label: 'Bond ID', model: 'bond_id', type: 'number' },
  { label: 'Payment Date', model: 'date', type: 'date' },
  { label: 'Type', model: 'type', select: true, items: ['Interest', 'Principal'] },
  { label: 'Amount', model: 'amount', type: 'number' },
  { label: 'Note', model: 'note', textarea: true },
]

// Summary box
const interestSummary = computed(() => {
  const total = interestHistory.value.reduce((acc, curr) => acc + parseFloat(curr.amount || 0), 0)
  const avgAmount = interestHistory.value.length ? total / interestHistory.value.length : 0
  return { total, avgAmount }
})

// Add or update
const save = async () => {
    await nextTick();
    const payload = {
        bond_id         : parseInt(editedItem.value.bond_id || 0),
        date            : editedItem.value.date || '',
        type            : editedItem.value.type || '',
        amount          : parseFloat(editedItem.value.amount || 0),
        note            : editedItem.value.note || '',
    }


    if (editedIndex.value === -1) {
        await addInterest({ input: payload })
        .then(fetchBonds)
        .catch(error => {
            console.log(error.message)
        });
    } else {
        await updateInterest({ entity_id: editedItem.value.entity_id, input: payload }).then(fetchBonds);
    }
    closeDialog();
}

// Delete
const deleteItem = async (item) => {
  loading.value = true
  await deleteInterest({ entity_id: item.entity_id }).then(fetchBonds)
}

// Methods
function fetchBonds() {
  loading.value = true
  refetch().then(({ data }) => {
    interestHistory.value = (data?.bondInterestHistories || []); 
  }).finally(() => {
    loading.value = false
  })
}

// Dialog actions
const openAddDialog = () => {
  editedIndex.value = -1
  editedItem.value = { bond_id: 0, date: '', type: '', amount: 0, note: '' }
  dialog.value = true
}

const editItem = (item) => {
  editedIndex.value = interestHistory.value.indexOf(item)
  editedItem.value = { ...item }
  dialog.value = true
}

function closeDialog() {
  dialog.value = false
  editedItem.value = { bond_id: 0, date: '', type: '', amount: 0, note: '' } // Reset form after close
}
</script>

<style scoped>
.bond-history-card {
  border: 1px solid var(--border);
}

.custom-data-table :deep(thead th) {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  background-color: #fafafa;
  border-bottom: 1px solid var(--border) !important;
}

.custom-data-table :deep(tbody tr) {
  transition: background-color 0.15s ease;
}

.custom-data-table :deep(tbody tr:hover) {
  background-color: #f8f9fb;
}

.custom-data-table :deep(tbody td) {
  font-size: 0.875rem;
  border-bottom: 1px solid #f1f2f4 !important;
}
</style>